/**
 * Upload queue UI — fixed dock at the bottom-right showing every in-flight
 * file with a progress bar. Drives the upload engine via startUpload().
 * Provides a useUploads() hook the host page calls to enqueue files.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type UploadItem, startUpload } from './engine';
import { formatBytes } from '../lib/format';
import { Icon } from '../components/brand';

interface UploadContext {
  enqueue: (files: File[], accountId: string, bucket: string, prefix: string) => void;
  items: UploadItem[];
}

const Ctx = createContext<UploadContext | null>(null);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const completeListeners = useRef<Set<() => void>>(new Set());

  const onUpdate = useCallback((item: UploadItem) => {
    setItems((prev) => prev.map((it) => (it.id === item.id ? { ...item } : it)));
  }, []);

  const onComplete = useCallback(() => {
    for (const fn of completeListeners.current) fn();
  }, []);

  const enqueue = useCallback(
    (files: File[], accountId: string, bucket: string, prefix: string) => {
      const newItems = files.map((file) =>
        startUpload(file, { accountId, bucket, keyPrefix: prefix }, { onUpdate, onComplete }),
      );
      setItems((prev) => [...prev, ...newItems]);
    },
    [onUpdate, onComplete],
  );

  const value = useMemo<UploadContext>(() => ({ enqueue, items }), [enqueue, items]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <UploadDock
        items={items}
        onClear={() =>
          setItems((prev) =>
            prev.filter((it) => it.status === 'uploading' || it.status === 'pending'),
          )
        }
      />
    </Ctx.Provider>
  );
}

export function useUploads() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUploads must be used inside <UploadProvider>');
  return ctx;
}

// ── Dock ─────────────────────────────────────────────────────────

function UploadDock({ items, onClear }: { items: UploadItem[]; onClear: () => void }) {
  const [collapsed, setCollapsed] = useState(false);
  if (items.length === 0) return null;

  const active = items.filter((it) => it.status === 'uploading' || it.status === 'pending').length;
  const done = items.filter((it) => it.status === 'done').length;
  const failed = items.filter((it) => it.status === 'error').length;

  return (
    <aside className="bm-upload-dock" aria-label="Upload queue">
      <header className="bm-upload-dock-head">
        <div className="bm-row bm-gap-8">
          <Icon.upload size={14} />
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Uploads</span>
          <span className="mono tnum" style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>
            {active} active · {done} done{failed > 0 ? ` · ${failed} failed` : ''}
          </span>
        </div>
        <div className="bm-row bm-gap-4">
          <button
            type="button"
            className="bm-btn bm-btn-sm"
            style={{ color: 'var(--bm-text-muted)' }}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? 'show' : 'hide'}
          </button>
          <button
            type="button"
            className="bm-btn bm-btn-sm"
            style={{ color: 'var(--bm-text-muted)' }}
            onClick={onClear}
            disabled={active > 0}
          >
            clear done
          </button>
        </div>
      </header>
      {!collapsed && (
        <div className="bm-upload-dock-body">
          {items.map((it) => (
            <UploadRow key={it.id} item={it} />
          ))}
        </div>
      )}
    </aside>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  const p = pct(item);
  const barColor: Record<UploadItem['status'], string> = {
    pending: 'var(--bm-info)',
    uploading: 'var(--bm-info)',
    done: 'var(--bm-success)',
    error: 'var(--bm-danger)',
    cancelled: 'var(--bm-text-faint)',
  };

  return (
    <div className="bm-upload-row">
      <div className="bm-row bm-gap-8" style={{ minWidth: 0 }}>
        <Icon.file size={13} />
        <span
          className="mono"
          style={{
            fontSize: 12,
            color: 'var(--bm-text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
          title={item.key}
        >
          {item.file.name}
        </span>
        {(item.status === 'uploading' || item.status === 'pending') && (
          <button
            type="button"
            className="bm-btn bm-btn-sm"
            style={{ color: 'var(--bm-text-muted)', padding: '0 6px', height: 20 }}
            onClick={() => item.abort()}
          >
            cancel
          </button>
        )}
      </div>
      <div
        className="bm-row bm-gap-8"
        style={{ marginTop: 4, fontSize: 11, color: 'var(--bm-text-muted)' }}
      >
        <span className="mono tnum">
          {formatBytes(item.uploaded)} / {formatBytes(item.size)}
        </span>
        <span className="bm-spacer" />
        <span className="mono tnum" style={{ color: barColor[item.status] }}>
          {item.status === 'uploading' || item.status === 'pending' ? `${p}%` : item.status}
        </span>
      </div>
      {item.error && (
        <div className="mono" style={{ fontSize: 11, color: 'var(--bm-danger)', marginTop: 4 }}>
          {item.error}
        </div>
      )}
      <div className="bm-upload-progress">
        <div
          className="bm-upload-progress-bar"
          style={{ width: `${p}%`, background: barColor[item.status] }}
        />
      </div>
    </div>
  );
}

function pct(it: UploadItem): number {
  if (it.size === 0) return it.status === 'done' ? 100 : 0;
  return Math.min(100, Math.round((it.uploaded / it.size) * 100));
}
